// src/api/apiClient.js
//
// Cliente HTTP compartido para todo el POS Compacto.
// Todos los endpoints del POS viven bajo /api/pos/*.
//
// Manejo del JWT:
// - Se guarda en localStorage como 'pos_token'.
// - Se envía automáticamente en cada request como Authorization: Bearer <token>.
// - Ante 401 se limpia el token y se dispara evento 'pos-auth-expired' que
//   AuthContext escucha para forzar el logout.

// ── URL del backend ────────────────────────────────────────
// Producción (npm run build) SIEMPRE apunta al backend HolaVeci en GCP
// Cloud Run. Se toma de REACT_APP_API_URL (.env.production) y, si falta o
// apunta a una dirección local, se usa el Cloud Run fijo. Esto evita que
// un build de producción salga apuntando a localhost o a la IP de la LAN
// por una variable de entorno que quedó puesta en la terminal
// (las variables del shell tienen prioridad sobre los archivos .env).
//
// Desarrollo (npm start) usa REACT_APP_API_URL de .env o localhost:5246.
const BACKEND_PRODUCCION = 'https://holaveci-backend-1033195680550.us-east5.run.app';

const esDireccionLocal = (url) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)(:\d+)?(\/|$)/i
    .test(url);

const urlDesdeEnv = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

export const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? (urlDesdeEnv && !esDireccionLocal(urlDesdeEnv) ? urlDesdeEnv : BACKEND_PRODUCCION)
    : (urlDesdeEnv || 'http://localhost:5246');

/** true si la app habla con un backend local (localhost o IP de la LAN). */
export const BACKEND_ES_LOCAL = esDireccionLocal(API_BASE_URL);

const STORAGE_TOKEN = 'pos_token';

// ── Manejo de token ────────────────────────────────────────
export const tokenStore = {
  get:   () => localStorage.getItem(STORAGE_TOKEN),
  set:   (t) => localStorage.setItem(STORAGE_TOKEN, t),
  clear: ()  => localStorage.removeItem(STORAGE_TOKEN),
};

// ── Error normalizado ──────────────────────────────────────
export class ApiError extends Error {
  constructor(status, mensaje, mensajeUsuario, payload) {
    super(mensaje);
    this.status         = status;
    this.mensajeUsuario = mensajeUsuario;
    this.payload        = payload;
  }
}

// ── Query string builder ──────────────────────────────────
function toQuery(params) {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => usp.append(k, x));
    else usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ── Núcleo ─────────────────────────────────────────────────
async function request(method, path, { body, query, skipAuth, headers } = {}) {
  const url = `${API_BASE_URL}${path}${toQuery(query)}`;
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
  };
  if (!skipAuth) {
    const t = tokenStore.get();
    if (t) opts.headers.Authorization = `Bearer ${t}`;
  }
  if (body !== undefined) opts.body = JSON.stringify(body);

  let res;
  try {
    res = await fetch(url, opts);
  } catch (netErr) {
    throw new ApiError(0, netErr.message,
      'No hay conexión con el servidor. Revisa tu internet.', null);
  }

  const contentType = res.headers.get('content-type') || '';
  let payload = null;
  if (contentType.includes('application/json')) {
    try { payload = await res.json(); } catch { payload = null; }
  } else {
    try { payload = await res.text(); } catch { payload = null; }
  }

  if (!res.ok) {
    if (res.status === 401 && !skipAuth) {
      tokenStore.clear();
      window.dispatchEvent(new Event('pos-auth-expired'));
    }
    // D-24: mensualidad vencida → el layout muestra la pantalla de pago.
    if (res.status === 402 && payload && payload.codigo === 'SUSCRIPCION_VENCIDA') {
      window.dispatchEvent(new Event('pos-suscripcion-vencida'));
    }
    const mensajeUsuario =
      (payload && (payload.mensaje || payload.error || payload.title)) ||
      (typeof payload === 'string' ? payload : null) ||
      `Error ${res.status}`;
    throw new ApiError(res.status, `HTTP ${res.status}`, mensajeUsuario, payload);
  }
  return payload;
}

// ── API pública ────────────────────────────────────────────
export const api = {
  get:    (path, opts)       => request('GET',    path, opts),
  post:   (path, body, opts) => request('POST',   path, { ...opts, body }),
  put:    (path, body, opts) => request('PUT',    path, { ...opts, body }),
  patch:  (path, body, opts) => request('PATCH',  path, { ...opts, body }),
  delete: (path, opts)       => request('DELETE', path, opts),

  /**
   * Sube archivo multipart. Recibe un FormData ya armado por el caller.
   * No serializa como JSON. Fetch pone el Content-Type con el boundary
   * automático cuando body es FormData.
   */
  postForm: async (path, formData) => {
    const url = `${API_BASE_URL}${path}`;
    const t = tokenStore.get();
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    let res;
    try {
      res = await fetch(url, { method: 'POST', headers, body: formData });
    } catch (netErr) {
      throw new ApiError(0, netErr.message,
        'No hay conexión con el servidor. Revisa tu internet.', null);
    }
    let payload = null;
    try { payload = await res.json(); } catch { payload = null; }
    if (!res.ok) {
      if (res.status === 401) {
        tokenStore.clear();
        window.dispatchEvent(new Event('pos-auth-expired'));
      }
      const mensajeUsuario =
        (payload && (payload.mensaje || payload.message || payload.error || payload.title)) ||
        `Error ${res.status}`;
      throw new ApiError(res.status, `HTTP ${res.status}`, mensajeUsuario, payload);
    }
    return payload;
  },
};
